import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  currentNcProvincialContributionsQueryOptions,
  ncProvincialContributionsDetailsExportQueryOptions,
  ncProvincialContributionsDetailsRawExportQueryOptions,
  ncProvincialContributionsDetailsSearchSchema,
  useNcProvincialContributionsById,
  useUpdateNcProvincialContributions,
} from '@/hooks/queries/crt/useNcProvincialContributions'
import { useLastUpdatedNcProvincialContributionsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateNcProvincialContributions } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NcProvincialContributionsDetailsDataTable from './-components/NcProvincialContributionsDetails/NcProvincialContributionsDetailsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/volume-forecasts/nc-provincial-contributions/$year/$month',
)({
  component: NcProvincialContributionsPeriod,
  validateSearch: (search) => ncProvincialContributionsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const crtPeriod = crtPeriodFromParams(params)
    const ncProvincialContributions = crtPeriod
      ? await queryClient.fetchQuery(currentNcProvincialContributionsQueryOptions(crtPeriod))
      : null

    return {
      crumb: ncProvincialContributions?.name ?? `${params.year}/${params.month}`,
      crtPeriod,
      ncProvincialContributionId: ncProvincialContributions
        ? String(ncProvincialContributions.id)
        : null,
    }
  },
})

function NcProvincialContributionsPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { crtPeriod, ncProvincialContributionId } = Route.useLoaderData()

  if (!ncProvincialContributionId) {
    return (
      <CrtImportPeriodNotFound
        period={crtPeriod ? formatMonthYear(parseDateOnly(crtPeriod)) : `${year}/${month}`}
        otherPeriodsTo="/crt/volume-forecasts/nc-provincial-contributions"
      />
    )
  }

  return (
    <NcProvincialContributionsDetails
      key={ncProvincialContributionId}
      ncProvincialContributionId={ncProvincialContributionId}
    />
  )
}

function NcProvincialContributionsDetails({
  ncProvincialContributionId,
}: {
  ncProvincialContributionId: string
}) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { month: _month, year: _year, ...apiSearch } = search
  const { data, isPending, isError } = useNcProvincialContributionsById(
    ncProvincialContributionId,
    {
      ...apiSearch,
      page: apiSearch.page ?? 1,
      pageSize,
    },
  )
  const { data: lastUpdatedNcProvincialContributionsTrail } =
    useLastUpdatedNcProvincialContributionsLog(ncProvincialContributionId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateNcProvincialContributions | null => {
    if (!data) return null

    const changes: UpdateNcProvincialContributions = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedNcProvincialContributionsTrail)

  const { mutate: updateNcProvincialContributions, isPending: isUpdatePending } =
    useUpdateNcProvincialContributions({
      ncProvincialContributionId: Number(ncProvincialContributionId),
    })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateNcProvincialContributions(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.ncProvincialContributions.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_volume_forecasts_configure"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('crtPeriod', { ns: 'crt' })}: {formatMonthYear(parseDateOnly(data.crtPeriod))}
          </Text>
          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      ncProvincialContributionsDetailsExportQueryOptions(
                        ncProvincialContributionId,
                        apiSearch,
                      ),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      ncProvincialContributionsDetailsRawExportQueryOptions(
                        ncProvincialContributionId,
                      ),
                    )
                  }
                  rowCount={data.detailsRowCount}
                  canExceedRowCountLimit
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/volume-forecasts/nc-provincial-contributions"
              uploadType="import-nc-provincial-contributions-details"
              uploadAttributes={{ 'nc-provincial-contribution-id': ncProvincialContributionId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <NcProvincialContributionsDetailsDataTable
        ncProvincialContributionId={ncProvincialContributionId}
        ncProvincialContributions={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_volume_forecasts_configure']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
